/**
 * API utility functions for authenticated requests
 * 
 * PHASE 2B: Prefers Firebase ID tokens, falls back to session tokens
 * AUTOMATIC TOKEN REFRESH: Handles 401 errors by refreshing token and retrying
 * KILL-SWITCH PATTERN: All fetch calls wrapped in try/catch/finally
 * to ensure loading states are always cleared on error
 */
import { auth } from '@/lib/firebase';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Track if we're currently refreshing to avoid multiple simultaneous refreshes
let isRefreshing = false;
let refreshPromise = null;

/**
 * Force refresh the Firebase ID token
 * Returns the new token or null if refresh failed
 */
const forceRefreshToken = async () => {
    // If already refreshing, wait for that to complete
    if (isRefreshing && refreshPromise) {
        return refreshPromise;
    }
    
    const user = auth.currentUser;
    if (!user) return null;
    
    isRefreshing = true;
    refreshPromise = (async () => {
        try {
            const newToken = await user.getIdToken(true); // true = force refresh
            console.log('🔄 Token force-refreshed due to 401');
            return newToken;
        } catch (e) {
            console.error('[AUTH] Force refresh failed:', e);
            return null;
        } finally {
            isRefreshing = false;
            refreshPromise = null;
        }
    })();
    
    return refreshPromise;
};

/**
 * Get the best available auth token
 * PHASE 2B: Prefers Firebase ID token, falls back to session token
 */
const getAuthToken = async (forceRefresh = false) => {
    // First try Firebase ID token (preferred)
    const user = auth.currentUser;
    if (user) {
        try {
            const idToken = await user.getIdToken(forceRefresh);
            return idToken;
        } catch (e) {
            console.warn('[AUTH] Failed to get Firebase ID token:', e);
        }
    }
    
    // Fallback to legacy session token (for guest users)
    const sessionToken = localStorage.getItem('session_token');
    if (sessionToken) {
        return sessionToken;
    }
    
    return null;
};

/**
 * Get authentication headers including Firebase ID token or session token
 * PHASE 2B: This is now an async function
 * @param {boolean} forceRefresh - Force refresh the token
 * @param {string} language - Language preference for Accept-Language header
 */
export const getAuthHeaders = async (forceRefresh = false, language = null) => {
    const headers = {
        'Content-Type': 'application/json'
    };
    
    // Add language header if provided, otherwise try to detect from localStorage
    const lang = language || localStorage.getItem('astrum_language') || navigator.language?.slice(0, 2) || 'en';
    headers['Accept-Language'] = lang;
    
    const token = await getAuthToken(forceRefresh);
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
};

/**
 * Get authentication headers synchronously (for legacy compatibility)
 * Falls back to session token only - use getAuthHeaders() when possible
 */
export const getAuthHeadersSync = () => {
    const headers = {
        'Content-Type': 'application/json'
    };
    
    const sessionToken = localStorage.getItem('session_token');
    if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
    }
    
    return headers;
};

/**
 * Make an authenticated GET request with error handling
 * Automatically retries once with refreshed token on 401 error
 * @param {string} endpoint - API endpoint
 * @param {object} options - Additional fetch options
 * @param {function} setLoading - Optional loading state setter (for kill-switch)
 */
export const authGet = async (endpoint, options = {}, setLoading = null) => {
    const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
    
    try {
        const headers = await getAuthHeaders();
        let response = await fetch(url, {
            method: 'GET',
            headers,
            credentials: 'include',
            ...options
        });
        
        // If 401 Unauthorized, try refreshing token and retry once
        if (response.status === 401 && auth.currentUser) {
            console.log('🔄 Got 401, attempting token refresh and retry...');
            const newToken = await forceRefreshToken();
            if (newToken) {
                const retryHeaders = await getAuthHeaders(true);
                response = await fetch(url, {
                    method: 'GET',
                    headers: retryHeaders,
                    credentials: 'include',
                    ...options
                });
                console.log('🔄 Retry response status:', response.status);
            }
        }
        
        return response;
    } catch (error) {
        console.error(`authGet error for ${endpoint}:`, error);
        throw error;
    } finally {
        // KILL-SWITCH: Always stop loading spinner
        if (setLoading) {
            setLoading(false);
        }
    }
};

/**
 * Make an authenticated POST request with error handling
 * Automatically retries once with refreshed token on 401 error
 * @param {string} endpoint - API endpoint
 * @param {object} body - Request body
 * @param {object} options - Additional fetch options
 * @param {function} setLoading - Optional loading state setter (for kill-switch)
 */
export const authPost = async (endpoint, body, options = {}, setLoading = null) => {
    const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
    
    try {
        const headers = await getAuthHeaders();
        let response = await fetch(url, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify(body),
            ...options
        });
        
        // If 401 Unauthorized, try refreshing token and retry once
        if (response.status === 401 && auth.currentUser) {
            console.log('🔄 Got 401 on POST, attempting token refresh and retry...');
            const newToken = await forceRefreshToken();
            if (newToken) {
                const retryHeaders = await getAuthHeaders(true);
                response = await fetch(url, {
                    method: 'POST',
                    headers: retryHeaders,
                    credentials: 'include',
                    body: JSON.stringify(body),
                    ...options
                });
                console.log('🔄 POST retry response status:', response.status);
            }
        }
        
        return response;
    } catch (error) {
        console.error(`authPost error for ${endpoint}:`, error);
        throw error;
    } finally {
        // KILL-SWITCH: Always stop loading spinner
        if (setLoading) {
            setLoading(false);
        }
    }
};

export default { getAuthHeaders, getAuthHeadersSync, authGet, authPost, forceRefreshToken };

